<script setup lang="ts">
import { XButton, XIcon } from '@clicksign/excalibur';
import { useDebounceFn, useResizeObserver } from '@vueuse/core';
import SignaturePad from 'signature_pad';
import type { Options } from 'signature_pad';
import { useI18n } from '#i18n';

import { ImgMime } from '../../constants/img';
import { canvasToBlob } from '../../utils/canvas-to-blob';
import { scalePadData } from '../../utils/scale-pad-data';

export type SignaturePadProps = {
  options?: Options
  imgUrl?: string
  readonly?: boolean
};

export type SignaturePadEmits = {
  (event: 'change', blob: Blob | undefined): void;
};

const DEFAULT_OPTIONS: Options = {
  penColor: '#000',
  dotSize: 1.5,
};

const props = defineProps<SignaturePadProps>();
const emit = defineEmits<SignaturePadEmits>();

const canvasElement = ref<HTMLCanvasElement>();
const signaturePad = shallowRef<SignaturePad>();
const padElement = ref<HTMLDivElement>();

const { t } = useI18n();

function resizeCanvas() {
  const canvas = canvasElement.value;
  const ctx = canvas?.getContext('2d');
  const pad = signaturePad.value;

  if (!canvas || !ctx || !pad || !padElement.value) return;

  const { clientWidth } = padElement.value;
  const ratio = Math.max(window.devicePixelRatio || 1, 1);

  const oldDimensions = {
    height: canvas.height,
    width: canvas.width,
  };

  const newDimensions = {
    height: Math.floor((clientWidth / 2) * ratio),
    width: Math.floor(clientWidth * ratio),
  };

  canvas.setAttribute('width', newDimensions.width.toString());
  canvas.setAttribute('height', newDimensions.height.toString());

  ctx.scale(ratio, ratio);

  if (props.imgUrl) {
    pad.fromDataURL(props.imgUrl);
    pad.off();
  }
  pad.fromData(scalePadData(pad.toData(), oldDimensions, newDimensions));
}

function clear() {
  if (!signaturePad.value) throw new Error('SignaturePad not initialized');

  signaturePad.value.clear();
  signaturePad.value?.on();
  emit('change', undefined);
}

async function toBlob(type: ImgMime = ImgMime.PNG) {
  if (!canvasElement.value) throw new Error('Canvas not initialized');

  return canvasToBlob(canvasElement.value, type);
}

const handleEndStroke = useDebounceFn(async () => emit('change', await toBlob()), 300);

useResizeObserver(padElement, () => requestAnimationFrame(resizeCanvas));

onMounted(() => {
  if (!canvasElement.value) return;

  signaturePad.value = new SignaturePad(canvasElement.value, {
    ...DEFAULT_OPTIONS,
    ...props.options,
  });

  signaturePad.value.addEventListener('endStroke', handleEndStroke);

  if (props.imgUrl) requestAnimationFrame(resizeCanvas);
});

onBeforeUnmount(() => {
  signaturePad.value?.off();
  signaturePad.value?.removeEventListener('endStroke', handleEndStroke);
});

watchEffect(() => {
  if (props.readonly) signaturePad.value?.off();
  else signaturePad.value?.on();
});

watch(() => props.imgUrl, () => {
  if (props.imgUrl && signaturePad.value) requestAnimationFrame(resizeCanvas);
});
</script>

<template>
  <section :class="$style.container">
    <header :class="$style.header">
      <div :class="$style.title">
        <XIcon
          icon="signature"
          left
          size="lg"
        />

        <i18nT
          keypath="document.rubricate.signatureModal.manuscriptTitle"
          tag="span"
        >
          <strong>{{ t('document.rubricate.signatureModal.sign') }}</strong>
        </i18nT>
      </div>

      <XButton
        v-if="!props.readonly"
        design="link"
        data-testid="padClearButton"
        size="sm"
        @click="clear()"
      >
        <span>
          {{ t('document.rubricate.signatureModal.clear') }}
        </span>

        <XIcon
          :class="$style.icon"
          icon="eraser"
          right
        />
      </XButton>
    </header>

    <div
      ref="padElement"
      :class="[$style.pad, !props.readonly && $style.dottedLine]"
    >
      <canvas
        ref="canvasElement"
        :class="$style.canvas"
        data-testid="signature-pad"
      />
    </div>
  </section>
</template>

<style module>
.container {
  position: relative;
  overflow: hidden;
  width: 100%;
  border-radius: var(--border-radius-medium);
  box-shadow: var(--shadow-small);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-small-x) var(--space-small-xx) var(--space-small-x) var(--space-small-x);
}

.dottedLine {
  background: url('../../assets/svg/dotted-line.svg') no-repeat bottom 30px center;
}

.icon {
  color: var(--color-brand-primary-400);
}

.canvas {
  display: block;
  max-width: 100%;
}
</style>
